/**
 * Copyright (c) 2010,2011,2012,2013,2014 Morgan Roderick http://roderick.dk
 * License: MIT - http://mrgnrdrck.mit-license.org
 *
 * https://github.com/mroderick/eventsJS
 */

(function(root, factory) {
  const events = {};
  root.events = events;

  const { define } = root;

  factory(events);

  // AMD support
  if (typeof define === 'function' && define.amd) {
    define(function() {
      return events;
    });

    // CommonJS and Node.js module support
  } else if (typeof exports === 'object') {
    if (module !== undefined && module.exports) {
      exports = module.exports = events; // Node.js specific `module.exports`
    }
    exports.events = events; // CommonJS module 1.1.1 spec
    module.exports = exports = events; // CommonJS
  }
})((typeof window === 'object' && window) || this, function(events) {
  let messages = {};
  let lastUid = -1;

  function hasKeys(obj) {
    let key;

    for (key in obj) {
      if (obj.hasOwnProperty(key)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Returns a function that throws the passed exception, for use as argument for setTimeout
   * @alias throwException
   * @function
   * @param { Object } ex An Error object
   */
  function throwException(ex) {
    return function reThrowException() {
      throw ex;
    };
  }

  function callSubscriberWithDelayedExceptions(subscriber, message, data) {
    try {
      subscriber(message, data);
    } catch (ex) {
      setTimeout(throwException(ex), 0);
    }
  }

  function callSubscriberWithImmediateExceptions(subscriber, message, data) {
    subscriber(message, data);
  }

  function deliverMessage(originalMessage, matchedMessage, data, immediateExceptions) {
    const subscribers = messages[matchedMessage];
    const callSubscriber = immediateExceptions
      ? callSubscriberWithImmediateExceptions
      : callSubscriberWithDelayedExceptions;
    let s;

    if (!messages.hasOwnProperty(matchedMessage)) {
      return;
    }

    for (s in subscribers) {
      if (subscribers.hasOwnProperty(s)) {
        callSubscriber(subscribers[s], originalMessage, data);
      }
    }
  }

  function createDeliveryFunction(message, data, immediateExceptions) {
    return function deliverNamespaced() {
      let topic = String(message);
      let position = topic.lastIndexOf('.');

      // deliver the message as it is now
      deliverMessage(message, message, data, immediateExceptions);

      // trim the hierarchy and deliver message to each level
      while (position !== -1) {
        topic = topic.substr(0, position);
        position = topic.lastIndexOf('.');
        deliverMessage(message, topic, data, immediateExceptions);
      }
    };
  }

  function messageHasSubscribers(message) {
    let topic = String(message);
    let found = Boolean(messages.hasOwnProperty(topic) && hasKeys(messages[topic]));
    let position = topic.lastIndexOf('.');

    while (!found && position !== -1) {
      topic = topic.substr(0, position);
      position = topic.lastIndexOf('.');
      found = Boolean(messages.hasOwnProperty(topic) && hasKeys(messages[topic]));
    }

    return found;
  }

  function publish(message, data, sync, immediateExceptions) {
    message = typeof message === 'symbol' ? message.toString() : message;

    const deliver = createDeliveryFunction(message, data, immediateExceptions);
    const hasSubscribers = messageHasSubscribers(message);

    if (!hasSubscribers) {
      return false;
    }

    if (sync === true) {
      deliver();
    } else {
      setTimeout(deliver, 0);
    }
    return true;
  }

  /**
   * Publishes the message, passing the data to it's subscribers
   * @function
   * @alias publish
   * @param { String } message The message to publish
   * @param {} data The data to pass to subscribers
   * @return { Boolean }
   */
  events.publish = function(message, data) {
    return publish(message, data, false, events.immediateExceptions);
  };

  /**
   * Publishes the the message synchronously, passing the data to it's subscribers
   * @function
   * @alias publishSync
   * @param { String } message The message to publish
   * @param {} data The data to pass to subscribers
   * @return { Boolean }
   */
  events.publishSync = function(message, data) {
    return publish(message, data, true, events.immediateExceptions);
  };

  /**
   * Subscribes the passed function to the passed message. Every returned token is unique and should be stored if you need to unsubscribe
   * @function
   * @alias subscribe
   * @param { String } message The message to subscribe to
   * @param { Function } func The function to call when a new message is published
   * @return { String }
   */
  events.subscribe = function(message, func) {
    if (typeof func !== 'function') {
      return false;
    }

    message = typeof message === 'symbol' ? message.toString() : message;

    // message is not registered yet
    if (!messages.hasOwnProperty(message)) {
      messages[message] = {};
    }

    // forcing token as String, to allow for future expansions without breaking usage
    // and allow for easy use as key names for the 'messages' object
    const token = `uid_${String(++lastUid)}`;
    messages[message][token] = func;

    // return token for unsubscribing
    return token;
  };

  /**
   * Subscribes the passed function to the passed message once
   * @function
   * @alias subscribeOnce
   * @param { String } message The message to subscribe to
   * @param { Function } func The function to call when a new message is published
   * @return { events }
   */
  events.subscribeOnce = function(message, func) {
    var token = events.subscribe(message, function() {
      // before func apply, unsubscribe message
      events.unsubscribe(token);
      func.apply(this, arguments);
    });
    return events;
  };

  /**
   * Clears all subscriptions
   * @function
   * @public
   * @alias clearAllSubscriptions
   */
  events.clearAllSubscriptions = function clearAllSubscriptions() {
    messages = {};
  };

  /**
   * Clear subscriptions by the topic
   * @function
   * @public
   * @alias clearAllSubscriptions
   */
  events.clearSubscriptions = function clearSubscriptions(topic) {
    let m;
    for (m in messages) {
      if (messages.hasOwnProperty(m) && m.indexOf(topic) === 0) {
        delete messages[m];
      }
    }
  };

  /**
   * Removes subscriptions
   *
   * - When passed a token, removes a specific subscription.
   *
   * - When passed a function, removes all subscriptions for that function
   *
   * - When passed a topic, removes all subscriptions for that topic (hierarchy)
   * @function
   * @public
   * @alias subscribeOnce
   * @param { String | Function } value A token, function or topic to unsubscribe from
   * @example // Unsubscribing with a token
   * var token = events.subscribe('mytopic', myFunc);
   * events.unsubscribe(token);
   * @example // Unsubscribing with a function
   * events.unsubscribe(myFunc);
   * @example // Unsubscribing from a topic
   * events.unsubscribe('mytopic');
   */
  events.unsubscribe = function(value) {
    const descendantTopicExists = function(topic) {
      let m;
      for (m in messages) {
        if (messages.hasOwnProperty(m) && m.indexOf(topic) === 0) {
          // a descendant of the topic exists:
          return true;
        }
      }

      return false;
    };
    const isTopic =
      typeof value === 'string' && (messages.hasOwnProperty(value) || descendantTopicExists(value));
    const isToken = !isTopic && typeof value === 'string';
    const isFunction = typeof value === 'function';
    let result = false;
    let m;
    let message;
    let t;

    if (isTopic) {
      events.clearSubscriptions(value);
      return;
    }

    for (m in messages) {
      if (messages.hasOwnProperty(m)) {
        message = messages[m];

        if (isToken && message[value]) {
          delete message[value];
          result = value;
          // tokens are unique, so we can just stop here
          break;
        }

        if (isFunction) {
          for (t in message) {
            if (message.hasOwnProperty(t) && message[t] === value) {
              delete message[t];
              result = true;
            }
          }
        }
      }
    }

    return result;
  };
});
